import React, { useState, useRef, useEffect } from "react";
import { buttonStyle } from "../utils/styleConstants";

const AnswerBox = ({
  userAnswer,
  setUserAnswer,
  handleAnswerSubmit,
  wrongAttempts,
  isWrongAnswer,
  onNumpadVisibilityChange, // Add this prop
}) => {
  const [showNumpad, setShowNumpad] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const answerBoxRef = useRef(null);
  const inputRef = useRef(null);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [isIOSDevice, setIsIOSDevice] = useState(false);

  // Detect mobile and iOS devices - improved iPad detection
  useEffect(() => {
    const checkDeviceType = () => {
      // Enhanced iOS detection that catches ALL iPad variants
      const ua = navigator.userAgent;
      const isIOS =
        /iPad|iPhone|iPod/.test(ua) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) ||
        (/Macintosh/.test(ua) && "ontouchend" in document);

      // Log detection for debugging
      console.log("Device detection:", {
        isIOS,
        userAgent: ua,
        platform: navigator.platform,
        touchPoints: navigator.maxTouchPoints,
      });

      // For iPad, always treat as both iOS and mobile
      setIsIOSDevice(isIOS);

      // If it's iOS, always treat as mobile regardless of screen size
      setIsMobileDevice(
        isIOS || /Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)
      );

      // Add a class to document.body for CSS targeting
      if (isIOS) {
        document.body.classList.add("ios-device");
      } else {
        document.body.classList.remove("ios-device");
      }
    };

    checkDeviceType();
    window.addEventListener("resize", checkDeviceType);

    return () => window.removeEventListener("resize", checkDeviceType);
  }, []);

  // Handle numpad button press
  const handleNumpadPress = (value) => {
    if (value === "backspace") {
      setUserAnswer(userAnswer.slice(0, -1));
    } else if (value === "clear") {
      setUserAnswer("");
    } else {
      setUserAnswer(userAnswer + value);
    }
  };

  // Add click outside listener to close numpad with animation when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showNumpad &&
        answerBoxRef.current &&
        !answerBoxRef.current.contains(event.target)
      ) {
        // Start fade-out animation
        setFadeOut(true);

        // After animation completes, hide the numpad
        setTimeout(() => {
          updateNumpadVisibility(false);
          setFadeOut(false);
        }, 300); // Match this to the CSS animation duration
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [showNumpad]);

  // When showing numpad, reset fadeOut
  useEffect(() => {
    if (showNumpad) {
      setFadeOut(false);
    }
  }, [showNumpad]);

  // Create a wrapper for handleAnswerSubmit that also hides the keypad
  const handleSubmit = () => {
    // Start the fade animation
    setFadeOut(true);

    // Hide the numpad after animation completes
    setTimeout(() => {
      updateNumpadVisibility(false);
      setFadeOut(false);
    }, 300);

    // Call the original submit handler
    handleAnswerSubmit();
  };

  // Generic handler for box click - enhanced to prevent focus on iOS
  const handleAnswerBoxClick = (e) => {
    // More aggressive on iOS
    if (isIOSDevice) {
      e.preventDefault();
      e.stopPropagation();

      // Force blur on any active element
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }

    updateNumpadVisibility(true);
  };

  // Update setShowNumpad to notify parent component
  const updateNumpadVisibility = (isVisible) => {
    setShowNumpad(isVisible);
    onNumpadVisibilityChange?.(isVisible);
  };

  return (
    <div
      ref={answerBoxRef}
      style={{
        position: "fixed",
        bottom: "20px",
        right: "20px",
        zIndex: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: "10px",
      }}
    >
      {/* Numeric Keypad */}
      {showNumpad && (
        <div
          style={{
            background: "#f3e1b2",
            padding: "15px",
            borderRadius: "12px",
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "8px",
            marginBottom: "10px",
            boxShadow: "0 -4px 10px rgba(0,0,0,0.1)",
            animation: fadeOut
              ? "fadeOut 0.3s ease-in-out forwards"
              : "fadeIn 0.3s ease-in-out",
            opacity: fadeOut ? 0 : 1,
          }}
        >
          {/* Number buttons 1-9 */}
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
            <button
              key={num}
              onClick={() => handleNumpadPress(num.toString())}
              style={{
                width: "50px",
                height: "50px",
                fontSize: "24px",
                fontWeight: "bold",
                backgroundColor: "#ffffff",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              {num}
            </button>
          ))}

          {/* Clear button */}
          <button
            onClick={() => handleNumpadPress("clear")}
            style={{
              width: "50px",
              height: "50px",
              fontSize: "16px",
              fontWeight: "bold",
              backgroundColor: "#ff9999",
              color: "#800000",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            C
          </button>

          {/* Zero button */}
          <button
            onClick={() => handleNumpadPress("0")}
            style={{
              width: "50px",
              height: "50px",
              fontSize: "24px",
              fontWeight: "bold",
              backgroundColor: "#ffffff",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            0
          </button>

          {/* Backspace button */}
          <button
            onClick={() => handleNumpadPress("backspace")}
            style={{
              width: "50px",
              height: "50px",
              fontSize: "20px",
              fontWeight: "bold",
              backgroundColor: "#9CDAED",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ⌫
          </button>
        </div>
      )}

      {/* Answer input area */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "clamp(5px, 2vw, 10px)",
          background: "#f3e1b2",
          padding: "clamp(8px, 2vw, 20px)",
          borderRadius: "12px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        {/* Use div instead of input for ALL iOS devices regardless of size */}
        {isMobileDevice || isIOSDevice ? (
          <div
            onClick={handleAnswerBoxClick}
            style={{
              padding: "8px 12px",
              fontSize: "clamp(20px, 4vw, 32px)",
              borderRadius: "8px",
              border: "2px solid #9CDAED",
              width: "clamp(60px, 15vw, 100px)",
              height: "clamp(40px, 8vw, 50px)",
              textAlign: "center",
              backgroundColor: "#ffffff",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              userSelect: "none",
              WebkitUserSelect: "none",
              MozUserSelect: "none",
              msUserSelect: "none",
              WebkitTouchCallout: "none",
              touchAction: "manipulation", // Prevent default touch actions
              WebkitAppearance: "none",
              WebkitTapHighlightColor: "transparent",
              outline: "none", // Remove focus outline
            }}
            role="textbox"
            aria-readonly="true"
            aria-label="Answer input"
            tabIndex="-1" // Use -1 to prevent any focus
          >
            {userAnswer || ""}
          </div>
        ) : (
          /* Regular input for desktop */
          <input
            ref={inputRef}
            type="number"
            inputMode="numeric"
            pattern="[0-9]*"
            value={userAnswer}
            onChange={(e) => setUserAnswer(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSubmit();
            }}
            onFocus={() => updateNumpadVisibility(true)}
            onClick={() => updateNumpadVisibility(true)}
            style={{
              padding: "8px 12px",
              fontSize: "clamp(20px, 4vw, 32px)",
              borderRadius: "8px",
              border: "2px solid #9CDAED",
              width: "clamp(60px, 15vw, 100px)",
              height: "clamp(40px, 8vw, 50px)",
              textAlign: "center",
              backgroundColor: "#ffffff",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              outline: "none",
              overflow: "hidden",
              WebkitAppearance: "none",
              MozAppearance: "textfield",
            }}
          />
        )}

        {/* Submit button */}
        <button
          style={{
            ...buttonStyle,
            margin: 0,
            backgroundColor: isWrongAnswer ? "#ff6b6b" : "#9CDAED",
            color: isWrongAnswer ? "#ffffff" : "#000000",
            border: "2px solid " + (isWrongAnswer ? "#ff5555" : "#88c9dc"),
            animation: isWrongAnswer ? "shake 0.5s" : "none",
            transition: "all 0.3s ease",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            fontSize: "22px",
            transform: "scale(1)",
            minHeight: "50px",
          }}
          onMouseEnter={(e) => (e.target.style.transform = "scale(1.05)")}
          onMouseLeave={(e) => (e.target.style.transform = "scale(1)")}
          onClick={handleSubmit}
        >
          Submit
        </button>
      </div>

      {/* Add enhanced styles for iOS keyboard prevention */}
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes fadeOut {
            from { opacity: 1; transform: translateY(0); }
            to { opacity: 0; transform: translateY(10px); }
          }
          
          /* iOS-specific fixes */
          .ios-device [role="textbox"], 
          .ios-device input {
            -webkit-user-select: none !important;
            user-select: none !important;
            -webkit-touch-callout: none !important;
            touch-action: manipulation !important;
            cursor: default !important;
            caret-color: transparent !important;
          }
          
          /* Fix specifically targeting iPads */
          @media (min-width: 768px) and (-webkit-min-device-pixel-ratio: 0) and (max-device-width: 1366px) {
            [role="textbox"] {
              pointer-events: auto !important; /* Allow click events */
              -webkit-user-select: none !important;
              user-select: none !important;
              -webkit-touch-callout: none !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default AnswerBox;
